import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Circle, 
  User, 
  Scale, 
  FileText, 
  Clock, 
  Bell, 
  MessageSquare,
  Folder,
  ChevronRight,
  Tag,
  Calendar,
  MessageCircle
} from 'lucide-react';

const SupportTicketItem = ({ item, mobile = false, desktop = false }) => {
  const navigate = useNavigate();
  
  const getStatusInfo = (status) => {
    switch (status) {
      case 'open': return { color: '#F97316', bg: '#FFF7ED', label: 'Open' };
      case 'in_progress': return { color: '#3B82F6', bg: '#EFF6FF', label: 'In Progress' };
      case 'awaiting_user': return { color: '#8B5CF6', bg: '#F5F3FF', label: 'Awaiting Reply' };
      case 'resolved': return { color: '#10B981', bg: '#F0FDF4', label: 'Resolved' };
      case 'closed': return { color: '#6B7280', bg: '#F9FAFB', label: 'Closed' };
      default: return { color: '#6B7280', bg: '#F9FAFB', label: status };
    }
  };

  const getPriorityInfo = (priority) => {
    switch (priority) {
      case 'urgent': return { color: '#DC2626', label: 'Urgent' };
      case 'high': return { color: '#EA580C', label: 'High' };
      case 'medium': return { color: '#D97706', label: 'Medium' };
      case 'low': return { color: '#059669', label: 'Low' };
      default: return { color: '#6B7280', label: priority };
    }
  };

  const getTicketType = (ticket) => {
    if (ticket.is_involved && ticket.dispute_id) {
      return { label: 'Dispute', bg: '#FEF2F2', color: '#DC2626', icon: Scale };
    } else if (ticket.is_owner) {
      return { label: 'My Ticket', bg: '#F0FDF4', color: '#059669', icon: User };
    }
    return null;
  };

  const status = getStatusInfo(item.status);
  const priority = getPriorityInfo(item.priority);
  const ticketType = getTicketType(item);
  const hasUnread = item.unread_count > 0;
  const formattedDate = new Date(item.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  // Mobile version
  if (mobile) {
    return (
      <div
        onClick={() => navigate(`/support/tickets/${item.ticket_id}`)}
        className="bg-white border border-gray-200 rounded-xl p-4 mb-3 cursor-pointer hover:shadow-sm transition-all active:bg-gray-50"
      >
        {/* Header with title and status */}
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-black text-sm line-clamp-2 flex-1 pr-2">
            {item.title}
          </h3>
          <div className="flex items-center gap-2">
            {hasUnread && (
              <div className="w-2 h-2 bg-[#ff660e] rounded-full"></div>
            )}
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Status and priority badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          <div 
            className="flex items-center px-2 py-1 rounded-lg"
            style={{ backgroundColor: status.bg }}
          >
            <Circle 
              size={8} 
              fill={status.color}
              color={status.color}
              className="mr-1"
            />
            <span 
              className="text-xs font-medium"
              style={{ color: status.color }}
            >
              {status.label}
            </span>
          </div>
          
          {ticketType && (
            <div 
              className="flex items-center px-2 py-1 rounded-lg"
              style={{ backgroundColor: ticketType.bg }}
            >
              <span 
                className="text-xs font-medium"
                style={{ color: ticketType.color }}
              >
                {ticketType.label}
              </span>
            </div>
          )}
          
          <div className="px-2 py-1 bg-gray-100 rounded-lg">
            <span 
              className="text-xs font-medium text-gray-600"
            >
              {priority.label}
            </span>
          </div>
        </div>

        {/* Category and date */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Folder size={12} className="text-gray-400" />
            <span className="text-xs text-gray-600">
              {item.category_name || 'General'}
            </span>
          </div>
          
          <div className="flex items-center gap-1">
            <Calendar size={12} className="text-gray-400" />
            <span className="text-xs text-gray-500">
              {formattedDate}
            </span>
          </div>
        </div>

        {/* Dispute info if applicable */}
        {item.dispute_id && (
          <div className="mt-3 px-3 py-2 bg-red-50 rounded-lg">
            <div className="flex items-center gap-2">
              <FileText size={12} className="text-red-600" />
              <span className="text-xs font-medium text-red-800">
                Dispute #{item.dispute_id}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop version
  if (desktop) {
    return (
      <div
        onClick={() => navigate(`/support/tickets/${item.ticket_id}`)}
        className="flex items-center gap-4 p-6 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
      >
        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }}></div>
        </div>

        {/* Ticket info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-gray-900 truncate">
              {item.title}
            </h3>
            
            {/* Badges */}
            <div className="flex items-center gap-2">
              <span 
                className="px-2 py-1 text-xs font-medium rounded-full"
                style={{ 
                  backgroundColor: status.bg,
                  color: status.color
                }}
              >
                {status.label}
              </span>
              
              {ticketType && (
                <span 
                  className="px-2 py-1 text-xs font-medium rounded-full"
                  style={{ 
                    backgroundColor: ticketType.bg,
                    color: ticketType.color
                  }}
                >
                  {ticketType.label}
                </span>
              )}
              
              <span 
                className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600"
              >
                {priority.label}
              </span>
            </div>
          </div>

          {/* Additional info */}
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Tag className="w-4 h-4" />
              <span>{item.category_name || 'General'}</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formattedDate}</span>
            </div>
            
            {item.dispute_id && (
              <div className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                <span className="font-medium text-red-600">Dispute #{item.dispute_id}</span>
              </div>
            )}
            
            {item.latest_message && (
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span className="truncate max-w-[200px]">{item.latest_message.message}</span>
              </div>
            )}
          </div>
        </div>

        {/* Unread count and arrow */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {hasUnread && (
            <div className="flex items-center justify-center w-6 h-6 bg-[#ff660e] text-white text-xs font-bold rounded-full">
              {item.unread_count > 99 ? '99+' : item.unread_count}
            </div>
          )}
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    );
  }

  // Default version (original card style)
  return (
    <div
      onClick={() => navigate(`/support/tickets/${item.ticket_id}`)}
      className="bg-white rounded-xl border border-gray-200 p-5 mb-3 cursor-pointer hover:shadow-md transition-shadow hover:border-gray-300"
    >
      {/* Header with status and priority */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-wrap gap-2">
          <div 
            className="flex items-center px-3 py-1.5 rounded-lg"
            style={{ backgroundColor: status.bg }}
          >
            <Circle 
              size={8} 
              fill={status.color}
              color={status.color}
              className="mr-2"
            />
            <span 
              className="text-xs font-semibold"
              style={{ color: status.color }}
            >
              {status.label}
            </span>
          </div>
          
          {ticketType && (
            <div 
              className="flex items-center px-2.5 py-1.5 rounded-lg"
              style={{ backgroundColor: ticketType.bg }}
            >
              {React.createElement(ticketType.icon, { 
                size: 12, 
                style: { color: ticketType.color, marginRight: 6 }
              })}
              <span 
                className="text-xs font-semibold"
                style={{ color: ticketType.color }}
              >
                {ticketType.label}
              </span>
            </div>
          )}
        </div>
        
        <div className="px-2.5 py-1 bg-gray-50 rounded-lg">
          <span 
            className="text-xs font-bold"
            style={{ color: priority.color }}
          >
            {priority.label}
          </span>
        </div>
      </div>

      {/* Ticket title and dispute info */}
      <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
        {item.title}
      </h3>
      
      {item.dispute_id && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-50 rounded-lg self-start">
          <FileText size={14} className="text-red-600" />
          <span className="text-xs font-semibold text-red-800">
            Dispute #{item.dispute_id} • Order #{item.order_id || 'N/A'}
          </span>
        </div>
      )}

      {/* Category and date */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Folder size={14} className="text-gray-500" />
          <span className="text-sm text-gray-600 font-medium">
            {item.category_name || 'General'}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          {hasUnread && (
            <div className="flex items-center gap-1 bg-orange-600 text-white px-2 py-1 rounded-full">
              <Bell size={12} />
              <span className="text-xs font-bold">{item.unread_count}</span>
            </div>
          )}
          <span className="text-sm text-gray-500">
            {new Date(item.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}
          </span>
        </div>
      </div>

      {/* Latest message preview */}
      {item.latest_message && (
        <div className="flex items-start gap-3 mt-4 pt-4 border-t border-gray-100">
          <MessageSquare size={14} className="text-gray-400 mt-0.5" />
          <p className="text-sm text-gray-600 flex-1 line-clamp-1">
            {item.latest_message.message}
          </p>
        </div>
      )}
    </div>
  );
};

export default SupportTicketItem;